import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useFiscalInvoices } from './useFiscalInvoices';
import type { Invoice } from './useFiscalInvoices';
import { invoiceNumber, invoiceSeries, formatAccessKey } from './accessKey';
import { useAuth } from '../../shared/auth/AuthContext';
import { formatCurrency, formatDocument, EMPTY, formatDateTime } from '../../shared/utils/format';
import { Badge, Button, LoadingSkeleton } from '@nexora/design-system';

/* ───────────────────────────────────────────────────────────────────────────
   Portado de base-html, slug `erpone-gest-o-de-documentos-fiscais-nf-e`.

   Da referência vieram: as abas por tipo de documento, os quatro indicadores
   no topo, a tabela densa com status como pill e a coluna Número/Série.

   Não vieram (não existem no backend, seriam números inventados):
   * painel "SEFAZ Status" com latência de autorização e estado de inutilização
     — não há monitor de status da SEFAZ nesta API;
   * "Recent Activity" com lotes transmitidos;
   * seleção múltipla com "Transmit / XML / Accountant" em lote — não há
     endpoint de transmissão em lote nem de envio ao contador.

   O número e a série saem da **chave de acesso** (layout da NF-e): a resposta
   não traz `ide.nNF`, e era isso que a tela lia antes.
   ─────────────────────────────────────────────────────────────────────────── */

const TYPE_TABS: { value: string; label: string }[] = [
  /* Os valores são os do enum `InvoiceType`: NFE, NFS, NFC. A tela mandava
     'NFSE'/'NFCE' e o filtro simplesmente não achava nada. */
  { value: 'ALL', label: 'Todos' },
  { value: 'NFE', label: 'NF-e (Eletrônica)' },
  { value: 'NFC', label: 'NFC-e (Consumidor)' },
  { value: 'NFS', label: 'NFS-e (Serviços)' },
];

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Rascunho',
  PENDING: 'Processando',
  AUTHORIZED: 'Autorizada',
  CANCELLED: 'Cancelada',
  REJECTED: 'Rejeitada',
  DENIED: 'Denegada',
};

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'neutral' | 'info'> = {
  DRAFT: 'neutral',
  PENDING: 'info',
  AUTHORIZED: 'success',
  CANCELLED: 'neutral',
  REJECTED: 'error',
  DENIED: 'error',
};

const STATUS_ICON: Record<string, string> = {
  DRAFT: 'edit_note',
  PENDING: 'cached',
  AUTHORIZED: 'check_circle',
  CANCELLED: 'cancel',
  REJECTED: 'error',
  DENIED: 'gpp_bad',
};

export function FiscalInvoiceListPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const navigate = useNavigate();

  const [typeFilter, setTypeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const { data, isLoading, error, refetch } = useFiscalInvoices(tenantId, {
    type: typeFilter !== 'ALL' ? typeFilter : undefined,
    status: statusFilter !== 'ALL' ? statusFilter : undefined,
    accessKey: search || undefined,
    page: page + 1,
    limit: pageSize,
  });

  const invoices = data?.data ?? [];
  const total = data?.total ?? 0;
  const counts = data?.counts;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const kpis = counts
    ? [
        { key: 'authorized', label: 'Autorizadas', value: counts.authorized, icon: 'check_circle', tone: 'ok' },
        { key: 'pending', label: 'Aguardando autorização', value: counts.pending, icon: 'cached', tone: 'info' },
        { key: 'rejected', label: 'Rejeitadas / denegadas', value: counts.rejected, icon: 'error', tone: 'bad' },
        { key: 'cancelled', label: 'Canceladas', value: counts.cancelled, icon: 'cancel', tone: 'muted' },
      ]
    : [];

  const recipientDoc = (inv: Invoice): string =>
    formatDocument(inv.destinatary?.cnpj ?? inv.destinatary?.cpf);

  return (
    <div className="fiscal-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Gestão de Documentos Fiscais
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Documentos emitidos e seu status na SEFAZ.
          </p>
        </div>
        <Button variant="primary" onClick={() => void navigate({ to: '/fiscal/invoices/new' })}>
          Emitir documento
        </Button>
      </div>

      {/* ── Abas por tipo, como na referência ── */}
      <div className="fiscal-tabs" role="tablist" aria-label="Tipo de documento">
        {TYPE_TABS.map((tab) => (
          <button
            key={tab.value}
            role="tab"
            type="button"
            aria-selected={typeFilter === tab.value}
            className={typeFilter === tab.value ? 'fiscal-tab is-active' : 'fiscal-tab'}
            onClick={() => {
              setTypeFilter(tab.value);
              setPage(0);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Indicadores: contagem do servidor, não da página ── */}
      {kpis.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-md mb-lg">
          {kpis.map((k) => (
            <div key={k.key} className="fiscal-kpi">
              <div className="fiscal-kpi__top">
                <span className="material-symbols-outlined" aria-hidden="true">
                  {k.icon}
                </span>
                <span className="font-label-md text-label-md text-on-surface-variant uppercase">
                  {k.label}
                </span>
              </div>
              <div className={`font-display-lg text-display-lg tabular-nums fiscal-kpi--${k.tone}`}>
                {k.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Filtros ── */}
      <div className="fiscal-toolbar">
        <input
          className="fiscal-control"
          placeholder="Buscar por chave de acesso..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          aria-label="Buscar por chave de acesso"
        />
        <select
          className="fiscal-control fiscal-control--select"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(0);
          }}
          aria-label="Filtrar por status"
        >
          <option value="ALL">Todos os status</option>
          {Object.entries(STATUS_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <span className="fiscal-count">
          {total} {total === 1 ? 'documento' : 'documentos'}
        </span>
      </div>

      {/* ── Tabela densa ── */}
      <div className="fiscal-card">
        {isLoading && (
          <div style={{ padding: '1rem' }}>
            <LoadingSkeleton count={6} height="40px" />
          </div>
        )}

        {error && !isLoading && (
          <div className="fiscal-state">
            <p className="fiscal-state__title">Erro ao carregar documentos fiscais</p>
            <p className="fiscal-state__detail">{(error as Error).message}</p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              Tentar novamente
            </Button>
          </div>
        )}

        {!isLoading && !error && invoices.length === 0 && (
          <div className="fiscal-state">
            <p className="fiscal-state__detail">Nenhum documento fiscal encontrado.</p>
          </div>
        )}

        {!isLoading && !error && invoices.length > 0 && (
          <>
            <div className="fiscal-table">
              <table>
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Número / Série</th>
                    <th>Emissão</th>
                    <th>Destinatário</th>
                    <th style={{ textAlign: 'right' }}>Valor total</th>
                    <th>Chave de acesso</th>
                    <th style={{ textAlign: 'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const num = invoiceNumber(inv.accessKey);
                    const serie = invoiceSeries(inv.accessKey);
                    return (
                      <tr
                        key={inv.id}
                        onClick={() => void navigate({ to: '/fiscal/invoices/$id', params: { id: inv.id } })}
                      >
                        <td>
                          <Badge variant={STATUS_VARIANT[inv.status] ?? 'neutral'} size="sm">
                            <span
                              className="material-symbols-outlined fiscal-status-icon"
                              aria-hidden="true"
                            >
                              {STATUS_ICON[inv.status] ?? 'help'}
                            </span>
                            {STATUS_LABEL[inv.status] ?? inv.status}
                          </Badge>
                        </td>
                        <td className="fiscal-mono">
                          {num === null ? EMPTY : `${String(num).padStart(7, '0')} / ${serie ?? 1}`}
                        </td>
                        <td className="fiscal-nowrap">{formatDateTime(inv.emissionDate)}</td>
                        <td>
                          <span className="fiscal-recipient">{inv.destinatary?.name ?? EMPTY}</span>
                          <span className="fiscal-recipient-doc">{recipientDoc(inv)}</span>
                        </td>
                        <td className="fiscal-num">{formatCurrency(inv.totals?.totalValue)}</td>
                        {/* Chave abreviada: a referencia nem mostra a chave na
                            lista, e a completa (44 digitos) empurra as acoes
                            para fora da tela. A busca por chave continua
                            existindo na barra de filtros, e o valor completo
                            fica no title e no detalhe. */}
                        <td className="fiscal-key" title={formatAccessKey(inv.accessKey)}>
                          {inv.accessKey
                            ? `${inv.accessKey.slice(0, 4)}…${inv.accessKey.slice(-6)}`
                            : EMPTY}
                        </td>
                        <td onClick={(e) => e.stopPropagation()} className="fiscal-actions">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              void navigate({ to: '/fiscal/invoices/$id', params: { id: inv.id } })
                            }
                          >
                            Detalhes
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="fiscal-footer">
              <span className="font-body-sm text-body-sm text-on-surface-variant tabular-nums">
                {page * pageSize + 1}–{Math.min(total, page * pageSize + invoices.length)} de {total}
              </span>
              <div className="flex gap-xs">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        .fiscal-page { padding: 1.5rem; }

        .fiscal-tabs { display: flex; gap: 0.25rem; border-bottom: 1px solid #c4c6cf; margin-bottom: 1.5rem; }
        .fiscal-tab {
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          padding: 0.5rem 0.875rem;
          font: inherit;
          font-size: 0.875rem;
          color: #43474e;
          cursor: pointer;
        }
        .fiscal-tab:hover { color: #002045; }
        .fiscal-tab.is-active { color: #002045; border-bottom-color: #002045; font-weight: 600; }

        .fiscal-kpi { background: #ffffff; border: 1px solid #c4c6cf; border-radius: 8px; padding: 1rem; box-shadow: 0 1px 2px rgba(0,0,0,.04); }
        .fiscal-kpi__top { display: flex; align-items: center; gap: 0.375rem; margin-bottom: 0.25rem; }
        .fiscal-kpi__top .material-symbols-outlined { font-size: 18px; color: #43474e; }
        .fiscal-kpi--ok { color: #1b7f4d; }
        .fiscal-kpi--info { color: #002045; }
        .fiscal-kpi--bad { color: #ba1a1a; }
        .fiscal-kpi--muted { color: #43474e; }

        .fiscal-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 0.75rem; margin-bottom: 1rem; }
        .fiscal-control {
          padding: 0.5rem;
          background: #f7f9fb;
          border: 1px solid #c4c6cf;
          border-radius: 2px;
          font: inherit;
          font-size: 0.875rem;
          color: #191c1e;
          min-width: 260px;
        }
        .fiscal-control--select { min-width: 200px; cursor: pointer; }
        .fiscal-control:focus { outline: none; border-color: #002045; box-shadow: 0 0 0 1px #002045; }
        .fiscal-count { margin-left: auto; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; color: #43474e; }

        .fiscal-card { background: #ffffff; border: 1px solid #c4c6cf; border-radius: 8px; box-shadow: 0 1px 2px rgba(0,0,0,.04); overflow: hidden; }
        .fiscal-table { overflow-x: auto; }
        .fiscal-table table { width: 100%; border-collapse: collapse; }
        .fiscal-table thead th {
          position: sticky;
          top: 0;
          z-index: 1;
          background: #f2f4f6;
          border-bottom: 1px solid #c4c6cf;
          padding: 0.5rem 0.75rem;
          text-align: left;
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-size: 10px;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #43474e;
          white-space: nowrap;
        }
        .fiscal-table tbody td { padding: 0.625rem 0.75rem; border-bottom: 1px solid #eceef0; font-size: 0.875rem; color: #191c1e; vertical-align: middle; }
        .fiscal-table tbody tr { cursor: pointer; }
        .fiscal-table tbody tr:hover { background: #f7f9fb; }
        .fiscal-mono, .fiscal-key { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; white-space: nowrap; }
        .fiscal-key { color: #43474e; font-size: 11px; }
        .fiscal-nowrap { white-space: nowrap; }
        .fiscal-num { text-align: right; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .fiscal-recipient { display: block; }
        .fiscal-recipient-doc { display: block; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: #43474e; }
        .fiscal-status-icon { font-size: 14px; vertical-align: -3px; margin-right: 2px; }
        .fiscal-actions { text-align: right; white-space: nowrap; }

        .fiscal-footer { display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 0.75rem 1rem; border-top: 1px solid #c4c6cf; }
        .fiscal-state { padding: 3rem 1rem; text-align: center; color: #43474e; font-size: 0.875rem; }
        .fiscal-state__title { margin: 0 0 0.5rem; font-weight: 600; color: #ba1a1a; }
        .fiscal-state__detail { margin: 0 0 1rem; }
      `}</style>
    </div>
  );
}
